'use client';

import {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ChevronDown, X } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { PopoverPortal, useAnchorRect } from '@/components/ui/popover-portal';
import { cn } from '@/lib/utils';
import { PanelSearchInput } from './panel-ui';

/**
 * Generic shape for a filter chip. `id` is opaque to the rail and
 * round-trips back through `onChipChange` — hosts decide what each
 * id maps to in their fetch URL.
 */
export type FilterChip<Id extends string> = {
  id: Id;
  label: ReactNode;
  icon?: ReactNode;
  count?: number;
};

export type SortOption<Id extends string> = {
  id: Id;
  label: ReactNode;
};

export type BulkAction = {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  tone?: 'default' | 'danger';
  onClick: () => void;
};

export type FilterRailSelection = {
  count: number;
  actions: BulkAction[];
  onClear: () => void;
};

type FilterRailProps<C extends string, S extends string> = {
  /** Bound search input. Debounce is the caller's responsibility. */
  search: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  searchHint?: string;

  /** Chip row. Empty array hides the chip row entirely. */
  chips: FilterChip<C>[];
  activeChip: C;
  onChipChange: (id: C) => void;

  /** Sort dropdown. Hidden when sortOptions is empty/undefined. */
  sort?: S;
  sortOptions?: SortOption<S>[];
  onSortChange?: (id: S) => void;

  /** Optional view-mode toggle (grid/list). Hidden when undefined. */
  viewMode?: 'grid' | 'list';
  onViewModeChange?: (mode: 'grid' | 'list') => void;

  /** Bulk-action bar — appears beneath chips when count > 0. */
  selection?: FilterRailSelection;

  /** Optional trailing slot rendered next to the sort dropdown. */
  trailing?: ReactNode;
};

/**
 * Compact filter rail used by all four My Library tabs.
 *
 * Layout (top to bottom):
 *   ┌─ search ─────────── sort ▾ ─ trailing ┐
 *   │ [chip] [chip] [chip] …                │
 *   │ [3 selected — Delete · Clear]         │  (only when selection.count > 0)
 *   └───────────────────────────────────────┘
 *
 * The rail does NOT fetch — hosts wire each callback to their state.
 * Generics let each tab type its chip and sort IDs without unsafe
 * string conversions at the call site.
 */
export function FilterRail<C extends string, S extends string>({
  search,
  onSearchChange,
  searchPlaceholder = 'Search…',
  searchHint,
  chips,
  activeChip,
  onChipChange,
  sort,
  sortOptions,
  onSortChange,
  viewMode,
  onViewModeChange,
  selection,
  trailing,
}: FilterRailProps<C, S>) {
  const t = useT();
  return (
    <div className="px-3 pt-2.5 pb-2 flex flex-col gap-2 select-none">
      <div className="flex items-center gap-1.5">
        {/* Shared panel search input — identical field across every view
            (autofocus + clear affordance live inside it). */}
        <PanelSearchInput
          className="flex-1"
          value={search}
          onChange={onSearchChange}
          placeholder={searchPlaceholder}
        />
        {sortOptions && sortOptions.length > 0 && sort !== undefined && onSortChange ? (
          <SortMenu options={sortOptions} value={sort} onChange={onSortChange} />
        ) : null}
        {viewMode && onViewModeChange ? (
          <ViewModeToggle value={viewMode} onChange={onViewModeChange} />
        ) : null}
        {trailing}
      </div>

      {searchHint && (
        <div className="px-1 text-[10px] text-muted-foreground/80 select-none">
          {searchHint}
        </div>
      )}

      {/* No negative-margin full-bleed on the chip row: `-mx-2` made it wider
          than the panel body, which put a horizontal scrollbar on the
          ANCESTOR scroller (the row hides only its own). */}
      {chips.length > 0 && (
        <div className="no-scrollbar flex items-center gap-1 overflow-x-auto">
          {chips.map((c) => {
            const active = c.id === activeChip;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => onChipChange(c.id)}
                aria-pressed={active}
                className={cn(
                  // Option A: filter chips are outline; the ACTIVE chip is the
                  // one dark element of its row (converges with panelChip).
                  'shrink-0 inline-flex items-center gap-1 h-7 rounded-full px-2.5 text-[11px] font-medium transition-colors',
                  active
                    ? 'bg-foreground text-background'
                    : 'border border-border/60 text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                )}
              >
                {c.icon && <span className="opacity-80">{c.icon}</span>}
                <span>{c.label}</span>
                {typeof c.count === 'number' && (
                  <span
                    className={cn(
                      'ml-0.5 tabular-nums text-[10px]',
                      active ? 'opacity-70' : 'text-muted-foreground',
                    )}
                  >
                    {c.count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {selection && selection.count > 0 && (
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-foreground/8 dark:bg-foreground/12">
          <span className="text-xs font-medium">
            {selection.count} selected
          </span>
          <div className="flex-1" />
          {selection.actions.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={a.onClick}
              className={cn(
                'inline-flex items-center gap-1 h-6 rounded-md px-2 text-[11px] font-medium transition-colors',
                a.tone === 'danger'
                  ? 'text-red-600 hover:bg-red-500/10 dark:text-red-400'
                  : 'hover:bg-foreground/10',
              )}
            >
              {a.icon}
              {a.label}
            </button>
          ))}
          <button
            type="button"
            aria-label={t('workspace.panels.clearSelection')}
            onClick={selection.onClear}
            className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-foreground/10"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
}

function SortMenu<S extends string>({
  options,
  value,
  onChange,
}: {
  options: SortOption<S>[];
  value: S;
  onChange: (id: S) => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const rect = useAnchorRect(open, triggerRef);
  const active = options.find((o) => o.id === value);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: PointerEvent) => {
      const t = triggerRef.current;
      if (!t) return;
      if (e.target instanceof Node && t.contains(e.target)) return;
      // Other clicks close after first letting PopoverPortal handle
      // its own internal click via dispatch order.
      setTimeout(() => setOpen(false), 0);
    };
    document.addEventListener('pointerdown', onPointer, true);
    return () => document.removeEventListener('pointerdown', onPointer, true);
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="menu"
        aria-expanded={open}
        className={cn(
          'shrink-0 inline-flex items-center gap-1 h-8 rounded-lg px-2 text-[11px] font-medium',
          'bg-foreground/5 hover:bg-foreground/10 dark:bg-foreground/10 dark:hover:bg-foreground/15',
        )}
      >
        <span className="truncate max-w-[80px]">{active?.label ?? 'Sort'}</span>
        <ChevronDown className="h-3 w-3 opacity-70" />
      </button>
      {open && rect && (
        <PopoverPortal
          role="menu"
          ariaLabel="Sort order"
          style={{
            position: 'fixed',
            top: rect.bottom + 4,
            left: Math.max(8, rect.right - 160),
            zIndex: 200,
            minWidth: 160,
          }}
        >
          <div
            className="glass-panel rounded-lg py-1 text-sm shadow-2xl"
            onClick={(e) => {
              // Stop the outer document handler from immediately
              // closing the menu before the inner button click fires.
              e.stopPropagation();
            }}
          >
            {options.map((o) => (
              <button
                key={o.id}
                type="button"
                role="menuitemradio"
                aria-checked={o.id === value}
                onClick={() => {
                  onChange(o.id);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs hover:bg-foreground/8',
                  o.id === value && 'font-medium',
                )}
              >
                <span>{o.label}</span>
                {o.id === value && (
                  <span className="h-1.5 w-1.5 rounded-full bg-foreground" />
                )}
              </button>
            ))}
          </div>
        </PopoverPortal>
      )}
    </>
  );
}

function ViewModeToggle({
  value,
  onChange,
}: {
  value: 'grid' | 'list';
  onChange: (m: 'grid' | 'list') => void;
}) {
  const t = useT();
  return (
    <div
      role="group"
      aria-label={t('workspace.panels.viewMode')}
      className="shrink-0 inline-flex h-8 items-center gap-px rounded-lg bg-foreground/5 dark:bg-foreground/10 p-px"
    >
      <ViewModeButton current={value} mode="grid" onChange={onChange} />
      <ViewModeButton current={value} mode="list" onChange={onChange} />
    </div>
  );
}

function ViewModeButton({
  current,
  mode,
  onChange,
}: {
  current: 'grid' | 'list';
  mode: 'grid' | 'list';
  onChange: (m: 'grid' | 'list') => void;
}) {
  const t = useT();
  const active = current === mode;
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={() => onChange(mode)}
      className={cn(
        'inline-flex h-[26px] w-[26px] items-center justify-center rounded-[6px] transition-colors',
        active ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
      )}
      aria-label={mode === 'grid' ? t('workspace.panels.gridView') : t('workspace.panels.listView')}
    >
      {mode === 'grid' ? <GridGlyph /> : <ListGlyph />}
    </button>
  );
}

function GridGlyph() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="2" y="2" width="5" height="5" rx="1" />
      <rect x="9" y="2" width="5" height="5" rx="1" />
      <rect x="2" y="9" width="5" height="5" rx="1" />
      <rect x="9" y="9" width="5" height="5" rx="1" />
    </svg>
  );
}

function ListGlyph() {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 4h10M3 8h10M3 12h10" strokeLinecap="round" />
    </svg>
  );
}
